import {QuoteStatus} from 'helpers/quotes'
import {get, isNil} from 'lodash'
import {getAllLeavesForBranch} from 'helpers/dataStructure'

export const DefaultExportColumns = [
  'companyName',
  'email',
  'forename',
  'surname',
  'jobTitle',
  'mobile',
  'phone',
  'type',
]

export const IncludeType = {
  EXCLUDE: 0,
  INCLUDE: 1,
  ONLY: 2,
}

export const orderFilters = [
  {
    key: 'draft',
    label: 'Created',
    status: QuoteStatus.draft,
    filterLabel: 'Draft Quotes',
    defaultSubFilter: '|o_createdDate:x:7:DAY',
  },
  {
    key: 'quoted',
    label: 'Quoted',
    status: QuoteStatus.quoted,
    filterLabel: 'Quoted Quotes',
    defaultSubFilter: '|o_quotedDate:x:7:DAY',
  },
  {
    key: 'confirmed',
    label: 'Confirmed',
    status: QuoteStatus.confirmed,
    filterLabel: 'Confirmed Jobs',
    defaultSubFilter: '|o_confirmedDate:x:7:DAY',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    status: QuoteStatus.delivered,
    filterLabel: 'Delivered Jobs',
    defaultSubFilter: '|o_deliveredDate:x:7:DAY',
  },
  {
    key: 'invoiced',
    label: 'Invoiced',
    status: QuoteStatus.invoiced,
    filterLabel: 'Invoiced Jobs',
    defaultSubFilter: '|o_invoicedDate:x:7:DAY',
  },
  {
    key: 'paid',
    label: 'Paid',
    status: QuoteStatus.paid,
    filterLabel: 'Paid Jobs',
    defaultSubFilter: '|o_paidDate:x:7:DAY',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    status: QuoteStatus.rejected,
    filterLabel: 'Rejected Quotes',
    defaultSubFilter: '|o_rejectedDate:x:7:DAY',
  },
]

export const createNewFilter = (key, fields, otherFilters = null) => {

  // Find next -ve ID for new filters
  let id = -1
  if (otherFilters) {
    otherFilters.forEach(filter => {
      if (filter.id <= id) id = filter.id - 1
    })
  }

  return {
    id,
    key,
    value: getDefaultFilterValue(key, fields),
  }
}

export const createNewSubFilter = (key) => {
  let value = ''
  switch (key) {
    case 'o_createdDate':
    case 'o_quotedDate':
    case 'o_confirmedDate':
    case 'o_invoicedDate':
    case 'o_deliveredDate':
    case 'o_paidDate':
    case 'o_rejectedDate':
      value = 'x:7:DAY'
      break
  }

  return {
    key,
    value,
  }
}

export const extractFilterParts = (filterValue) => {
  let value, subFilters = []
  const values = filterValue ? filterValue.split('|') : []
  if (values.length > 0) value = values[0]
  if (values.length > 1 && values[1].length > 0) subFilters = values.slice(1).map(p => extractSubFilter(p))
  return {value, subFilters}
}

export const encodeFilterParts = (filterValue, subFilters) => {
  return [filterValue, ...(subFilters || []).map(f => encodeSubFilter(f))].join('|')
}

const extractSubFilter = (s) => {
  const parts = s ? s.split(':') : []
  let key = '', value = ''
  if (parts.length > 0) key = parts[0]
  if (parts.length > 1) value = parts.slice(1).join(':')
  return {key, value}
}

const encodeSubFilter = (filter) => {
  return `${filter.key}:${filter.value}`
}

export const getDefaultFilterValue = (key, fields) => {
  const type = key.charAt(0)

  switch (type) {
    case 'f':     // contact field
    case 'c':     // company field
    case 'l':     // location field
    case 't': {   // user-field (tag)
      const field = fields.find(f => f.key === key)
      if (field === null) return ''

      return getDefaultFilterValueForDataType(field.dataType)
    }

    case 'e':
      return 'a::e::DAY:::p' // activity

    case 'o': {
      let subfilter = ''
      if (key.length > 2) {
        const filterKey = key.substring(2)
        const orderFilter = orderFilters.find(f => f.key === filterKey)
        if (orderFilter) subfilter = orderFilter.defaultSubFilter
      }
      return 'a:g::e::p' + subfilter  // order
    }
    default:
      return ''
  }
}

export const getDefaultFilterValueForDataType = (dataType) => {

  switch (dataType) {
    case 'Email':
    case 'Phone':
    case 'Mobile':
    case 'Memo':
    case 'String':
      return 'c:'
    case 'Currency':
    case 'Company List':
    case 'Number':
      return 'e:'
    case 'List':
      return 'e:'
    case 'Multi List':
      return 'a:'
    case 'Boolean':
      return 'y'
    case 'Date':
      return 'd:|DAY'
    default:
      return ''
  }
}

export const getActivitySubFilters = () => getAllSubFilters().filter(f => f.key.charAt(0) === 'e')

export const getOrderSubFilters = () => getAllSubFilters().filter(f => f.key.charAt(0) === 'o')

export const getAllSubFilters = () => ([
  {key: 'e_creator', label: 'Created By', dataType: 'List'},
  {key: 'e_owner', label: 'Owned By', dataType: 'List'},
  {key: 'e_allocated', label: 'Allocated To', dataType: 'Multi List'},
  {key: 'e_completed', label: 'Completed', dataType: 'List'},
  {key: 'e_status', label: 'Status', dataType: 'Multi List'},
  {key: 'e_priority', label: 'Priority', dataType: 'Multi List'},
  {key: 'o_createdDate', label: 'Created Date', dataType: 'Date'},
  {key: 'o_quotedDate', label: 'Quoted Date', dataType: 'Date'},
  {key: 'o_confirmedDate', label: 'Confirmed Date', dataType: 'Date'},
  {key: 'o_invoicedDate', label: 'Invoiced Date', dataType: 'Date'},
  {key: 'o_deliveredDate', label: 'Delivered Date', dataType: 'Date'},
  {key: 'o_paidDate', label: 'Paid Date', dataType: 'Date'},
  {key: 'o_rejectedDate', label: 'Rejected Date', dataType: 'Date'},
  {key: 'o_creator', label: 'Created By', dataType: 'List'},
  {key: 'o_estimator', label: 'Estimated By', dataType: 'List'},
  {key: 'o_salesman', label: 'Salesperson', dataType: 'List'},
  {key: 'o_status', label: 'Status', dataType: 'List'},
  {key: 'o_type', label: 'Job Type', dataType: 'List'},
  {key: 'o_orderReason', label: 'Order Reason', dataType: 'List'},
  {key: 'o_rejectReason', label: 'Reject Reason', dataType: 'List'},
])

export const nullContactList = {
  id: -1,
  name: null,
  categoryId: null,
  expression: null,
  locationId: null,
  regionId: null,
  siteId: null,
  homeType: null,
  dormantType: null,
  summary: null,
  notes: null,
  active: false,
  parentId: null,
  ownerId: null,
  creatorId: null,
  createdDate: null,
  modifiedDate: null,
}

export const ContactListType = {
  ROOT: 0,
  OWNER: -1,
  ALLOCATED_BY_ME: -2,
  TEAM: -3,
}

export const getRootCategory = () => {
  return {
    id: ContactListType.ROOT,
    name: 'All Contact Lists',
    parentId: null,
  }
}

export const getOwnerCategory = () => {
  return {
    id: ContactListType.OWNER,
    name: 'Allocated to me',
    parentId: null,
  }
}

export const getAllocatedByMeCategory = () => {
  return {
    id: ContactListType.ALLOCATED_BY_ME,
    name: 'Allocated by me',
    parentId: null,
  }
}

export const getTeamCategory = () => {
  return {
    id: ContactListType.TEAM,
    name: 'Assigned to my team',
    parentId: null,
  }
}

export const getDefaultCategories = () => {
  return [
    getOwnerCategory(),
    getAllocatedByMeCategory(),
  ]
}

export const isInOwnerCategory = (categoryId) => {
  return categoryId === ContactListType.OWNER
}

export const isInCreatedByMeCategory = (categoryId) => {
  return categoryId === ContactListType.ALLOCATED_BY_ME
}

export const isInTeamCategory = (categoryId) => {
  return categoryId === ContactListType.TEAM
}

export const getCategory = (categoryId, categories) => {
  switch (categoryId) {
    case ContactListType.ROOT:
      return getRootCategory()
    case ContactListType.OWNER:
      return getOwnerCategory()
    case ContactListType.ALLOCATED_BY_ME:
      return getAllocatedByMeCategory()
    case ContactListType.TEAM:
      return getTeamCategory()
    default:
      return categories.find(category => category.id === categoryId)
  }
}

export const getContactLists = (lists, categories, selectedCategoryId, userContactId) => {
  switch (selectedCategoryId) {
    case ContactListType.ROOT:
      return lists
          .filter(list => list.ownerId === null)
          .filter(list => list.creatorId === null)
    case ContactListType.OWNER:
      return lists
          .filter(list => list.ownerId === userContactId)
    case ContactListType.ALLOCATED_BY_ME:
      return lists
          .filter(list => list.creatorId === userContactId)
    default:

      const contactLists = lists
          .slice()
          .filter(list => list.ownerId === null)
          .filter(list => list.creatorId === null)

      return getAllLeavesForBranch(
          contactLists,
          categories,
          selectedCategoryId,
          'categoryId',
          'id',
          'parentId')
  }
}

export const getContactListCount = (lists, categories, categoryId, userContactId) => {
  return getContactLists(lists, categories, categoryId, userContactId).length
}

export const isRootContactListCategory = (categoryId) => {
  return categoryId === ContactListType.ROOT
}

export const isAllocateToVisible = (ownerId, selectedCategoryId) => {
  return isNil(ownerId) ||
      ownerId === null ||
      isRootContactListCategory(selectedCategoryId)
}

export const isCategoryActionAllowed = (categoryId) => {
  switch (categoryId) {
    case ContactListType.OWNER:
      return false
    case ContactListType.ALLOCATED_BY_ME:
      return false
    case ContactListType.TEAM:
      return false
    default:
      return true
  }
}

export const isMainCategory = (categoryId) => {
  return categoryId === ContactListType.ROOT ||
      categoryId === ContactListType.OWNER ||
      categoryId === ContactListType.ALLOCATED_BY_ME ||
      categoryId === ContactListType.TEAM
}

export const isMainCategoryExcludingRoot = (categoryId) => {
  return categoryId === ContactListType.OWNER ||
      categoryId === ContactListType.ALLOCATED_BY_ME ||
      categoryId === ContactListType.TEAM
}

export const getIsModifiedDateVisible = (contactList) => {
  return contactList.modifiedDate > contactList.createdDate
}

export const getIsCreatorVisible = (creator, selectedCategoryId) => {
  return !isNil(creator) && isInOwnerCategory(selectedCategoryId)
}

export const getIsOwnerVisible = (owner, selectedCategoryId) => {
  return !isNil(owner) && isInCreatedByMeCategory(selectedCategoryId)
}

export const getIsNotesHidden = (notes, selectedCategoryId) => {
  return isNil(notes) &&
      (isInOwnerCategory(selectedCategoryId) || isInCreatedByMeCategory(selectedCategoryId))
}

export const isNonOwnerActionsVisible = (selectedCategoryId) => {
  return !isInOwnerCategory(selectedCategoryId) &&
      !isInCreatedByMeCategory(selectedCategoryId)
}

export const AllocationType = {
  USERS: 1,
  TEAM: 2,
}

export const getHistoryStat = (contact) => {
  const historyStat = get(contact, 'historystat', null)
  return isNil(historyStat)
      ? {
        totalCalls: 0,
        totalMeetings: 0,
        totalToDos: 0,
        totalNotes: 0,
        totalTasks: 0,
        totalEmails: 0,
        totalQuoteFollowUps: 0,
        totalRejectedActivities: 0,
        totalCompletedCalls: 0,
        totalPendingMeetings: 0,
      }
      : historyStat
}

import {urls} from 'helpers/url'

export const getViewContactListUrl = (contactListId) => {
  return `${urls.contacts.contactListsUrl}/${contactListId}/view`
}

export const getEditContactListUrl = (contactListId) => {
  return `${urls.contacts.contactListsUrl}/${contactListId}`
}
